import { supabase } from '../lib/supabase';

// Get all verified notaries (public access)
export const getAllNotaries = async () => {
  try {
    const { data, error } = await supabase?.from('notaries')?.select(`
        *,
        user_profile:user_profiles(id, full_name, email, phone, avatar_url)
      `)?.eq('is_verified', true)?.order('notary_name');

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch notaries' };
  }
};

// Get notary by ID
export const getNotaryById = async (id) => {
  try {
    const { data, error } = await supabase?.from('notaries')?.select(`
        *,
        user_profile:user_profiles(id, full_name, email, phone, avatar_url)
      `)?.eq('id', id)?.eq('is_verified', true)?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to fetch notary' };
  }
};

// Get notaries by specialization
export const getNotariesBySpecialization = async (specialization) => {
  try {
    const { data, error } = await supabase?.from('notaries')?.select(`
        *,
        user_profile:user_profiles(id, full_name, email, phone, avatar_url)
      `)?.contains('specializations', [specialization])?.eq('is_verified', true)?.order('notary_name');

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch notaries by specialization' };
  }
};

// Create notary profile (authenticated users)
export const createNotary = async (notaryData) => {
  try {
    const { data, error } = await supabase?.from('notaries')?.insert([notaryData])?.select()?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to create notary profile' };
  }
};

// Update notary profile (owner or admin)
export const updateNotary = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('notaries')?.update(updates)?.eq('id', id)?.select()?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to update notary profile' };
  }
};

// Delete notary profile (owner or admin)
export const deleteNotary = async (id) => {
  try {
    const { error } = await supabase?.from('notaries')?.delete()?.eq('id', id);

    if (error) {
      return { error: error?.message };
    }

    return { error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { error: 'Failed to delete notary profile' };
  }
};

// Search notaries by location or name
export const searchNotaries = async (searchTerm) => {
  try {
    const { data, error } = await supabase?.from('notaries')?.select(`
        *,
        user_profile:user_profiles(id, full_name, email, phone, avatar_url)
      `)?.eq('is_verified', true)?.or(`notary_name.ilike.%${searchTerm}%,office_address.ilike.%${searchTerm}%`)?.order('notary_name');

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to search notaries' };
  }
};

// Get appointment statistics for notaries
export const getAppointmentStats = async () => {
  try {
    // Get current month start and end dates
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    // Get appointments count for this month
    const { data: thisMonthData, error: thisMonthError } = await supabase
      ?.from('appointments')
      ?.select('id', { count: 'exact' })
      ?.gte('scheduled_date', currentMonthStart?.toISOString())
      ?.lt('scheduled_date', nextMonthStart?.toISOString())
      ?.not('notary_id', 'is', null);

    if (thisMonthError) {
      console.error('Error fetching this month appointments:', thisMonthError);
      return { data: { thisMonth: 0 }, error: thisMonthError?.message };
    }

    // Get total appointments count
    const { data: totalData, error: totalError } = await supabase
      ?.from('appointments')
      ?.select('id', { count: 'exact' })
      ?.not('notary_id', 'is', null);

    if (totalError) {
      console.error('Error fetching total appointments:', totalError);
      return { data: { thisMonth: thisMonthData?.length || 0, total: 0 }, error: totalError?.message };
    }

    return { 
      data: { 
        thisMonth: thisMonthData?.length || 0,
        total: totalData?.length || 0
      }, 
      error: null 
    };
  } catch (error) {
    console.error('Error in getAppointmentStats:', error);
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: { thisMonth: 0, total: 0 }, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: { thisMonth: 0, total: 0 }, error: 'Failed to fetch appointment statistics' };
  }
};

// Create the service object with all functions
const notaryService = {
  getAllNotaries,
  getNotaryById,
  getNotariesBySpecialization,
  createNotary,
  updateNotary,
  deleteNotary,
  searchNotaries,
  getAppointmentStats
};

// Export both default and named exports for maximum compatibility
export default notaryService;
export { notaryService };
function getNotaryReviews(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getNotaryReviews is not implemented yet.', args);
  return null;
}

export { getNotaryReviews };